/**
 * Attachment reference codec.
 *
 * The macOS app stores machine-local `file://` URLs in note bodies and
 * converts them to portable `jnote-att://<id>/<filename>` references on the
 * way to the bucket. The Web client has no local filesystem, so it works
 * directly in the portable form — but it must still parse and emit that
 * scheme identically.
 */

export const ATTACHMENT_SCHEME = 'jnote-att';

/**
 * Matches a portable reference end to end, tolerating the raw (unencoded)
 * characters macOS writes verbatim — most importantly SPACES in filenames
 * such as `jnote-att://<uuid>/Screenshot 2026-09-11 at 09.09.25.png`.
 *
 * The id segment stays strict (UUIDs never contain spaces), but the filename
 * run may span spaces: a space-bearing reference only ever appears wrapped in
 * a delimiter (`![…](…)`, `[…](…)`, `src="…"`), so it is safe to keep
 * consuming until a markdown/URL delimiter or a line break. Trailing spaces
 * before the closing delimiter are trimmed in code.
 */
const REFERENCE_PATTERN = /jnote-att:\/\/([^/\s)\]"'<>]+)\/([^\n)\]"'<>]+)/g;
const ID_PATTERN = /jnote-att:\/\/([^/\s)\]"'<>]+)\//g;

/** Decodes a filename segment that may arrive raw or percent-encoded. */
function decodeFilename(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    // A literal `%` that is not a valid escape stays as written.
    return raw;
  }
}

/** Rewrites `file://` URLs under the attachments root into portable form. */
export function toPortable(content: string, attachmentsRoot?: string): string {
  if (!attachmentsRoot) return content;

  const prefix = attachmentsRoot.endsWith('/') ? attachmentsRoot : `${attachmentsRoot}/`;
  return content.replace(/file:\/\/[^\s)\]"'<>]+/g, (raw) => {
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      return raw;
    }
    if (url.protocol !== 'file:') return raw;

    const path = decodeURIComponent(url.pathname);
    if (!path.startsWith(prefix)) return raw;

    const relative = path.slice(prefix.length);
    const parts = relative.split('/');
    if (parts.length !== 2 || !parts[0] || !parts[1]) return raw;

    return `${ATTACHMENT_SCHEME}://${parts[0]}/${parts[1]}`;
  });
}

/**
 * Resolves portable references to fetchable URLs. On macOS this produces
 * `file://` paths for the editor; on the Web it produces backend-proxied
 * object URLs.
 *
 * The resolver receives the DECODED filename, while the match it replaces is
 * the raw text as written — so bodies with raw spaces (macOS) and bodies with
 * percent-encoded names (Web insertion) both rewrite correctly.
 */
export function toLocal(
  content: string,
  resolve: (id: string, filename: string) => string,
): string {
  return content.replace(REFERENCE_PATTERN, (raw) => {
    const match = /^jnote-att:\/\/([^/]+)\/([\s\S]+)$/.exec(raw);
    if (!match) return raw;
    const [, id, encoded] = match;
    if (!id || !encoded) return raw;
    const filename = decodeFilename(encoded.trimEnd());
    if (!filename) return raw;
    return resolve(id, filename);
  });
}

/** Attachment folder ids referenced by a note body. */
export function referencedAttachmentIDs(content: string): Set<string> {
  const ids = new Set<string>();
  const portable = content;
  for (const match of portable.matchAll(ID_PATTERN)) {
    const id = match[1];
    if (id) ids.add(id);
  }
  return ids;
}

export interface AttachmentReference {
  id: string;
  filename: string;
  /** Canonical (percent-encoded) `jnote-att://` literal for map keys. */
  reference: string;
}

/** Every distinct `jnote-att://` reference in a note body. */
export function attachmentReferences(content: string): AttachmentReference[] {
  const seen = new Set<string>();
  const result: AttachmentReference[] = [];
  for (const match of content.matchAll(REFERENCE_PATTERN)) {
    const [, id, encoded] = match;
    if (!id || !encoded) continue;
    const filename = decodeFilename(encoded.trimEnd());
    if (!filename) continue;
    const key = `${id}/${filename}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({ id, filename, reference: attachmentURL(id, filename) });
  }
  return result;
}

/** Builds a portable reference for insertion into a note body. */
export function attachmentURL(id: string, filename: string): string {
  return `${ATTACHMENT_SCHEME}://${id}/${encodeURIComponent(filename)}`;
}
